import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Heart, Quote } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

export const About = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const content = {
    en: {
      title: "OUR STORY",
      subtitle: "Why we fight",
      story: [
        "Walking home at night. There's a man behind me and I don't know if he's dangerous. I'm scared, I don't want to turn around too much to avoid drawing attention but I can hear his footsteps getting closer. I don't know if he'll do something to me. I walk faster, I accelerate. I imagine all the worst possible scenarios.",
        "I don't want to feel fear. I don't want to be raped. I don't want to be harassed or assaulted.",
        "The justice system does nothing to make me feel safe. After reporting my sexual aggressor, I'm still waiting for trial. After many years, I still have to relive this shit so that in the end, the sexual aggressor walks free and unpunished.",
        "Being able to breathe peacefully and live without the hypervigilance that characterizes being a woman is a right. A right that has been stolen from us.",
        "That's why Fight like a GYAL exists. Because waiting for the world to change is not an option. Because our safety cannot depend on others. Because fear should change sides.",
        "We're not promoting violence. We're promoting confidence. The confidence of knowing that if something happens, you have the tools to defend yourself. The confidence of walking home without checking over your shoulder every three seconds.",
        "This is for every woman who has felt that knot in her stomach walking alone at night. For every woman who has had to change her route, her clothes, her schedule to feel safer. For every woman who has been told \"be careful\" instead of men being told \"don't attack.\"",
        "We fight like GYALs. And now, so can you."
      ],
      cta: "Join the movement",
      founder: "— The Founder"
    },
    es: {
      title: "NUESTRA HISTORIA",
      subtitle: "Por qué luchamos",
      story: [
        "Caminando de vuelta a casa por la noche. Hay un hombre detrás de mí y no sé si es peligroso. Tengo miedo, no quiero girarme mucho para no llamar la atención pero escucho sus pasos acercarse. No sé si me hará algo. En todo caso camino más rápido, acelero. Me imagino todos los peores escenarios posibles.",
        "No quiero sentir miedo. No quiero ser violada. No quiero ser acosada ni agredida.",
        "La justicia no hace nada para que me sienta segura. Después de denunciar a mi agresor sexual aún estoy en espera de juicio. Después de muchos años aún me toca revivir esta mierda para que al final, el agresor sexual salga libre e impune.",
        "Poder respirar tranquila y vivir tranquila sin la hipervigilancia que caracteriza ser mujer es un derecho. Un derecho que nos han robado.",
        "Por eso existe Fight like a GYAL. Porque esperar a que el mundo cambie no es una opción. Porque nuestra seguridad no puede depender de otros. Porque el miedo tiene que cambiar de bando.",
        "No promovemos la violencia. Promovemos la confianza. La confianza de saber que si algo pasa, tienes las herramientas para defenderte. La confianza de volver a casa sin mirar por encima del hombro cada tres segundos.",
        "Esto es para cada mujer que ha sentido ese nudo en el estómago caminando sola de noche. Para cada mujer que ha tenido que cambiar su ruta, su ropa, su horario para sentirse más segura. Para cada mujer a la que le han dicho \"ten cuidado\" en vez de decirle a los hombres \"no agredas.\"",
        "Luchamos como GYALs. Y ahora, tú también puedes."
      ],
      cta: "Únete al movimiento",
      founder: "— La Fundadora"
    },
    fr: {
      title: "NOTRE HISTOIRE",
      subtitle: "Pourquoi nous nous battons",
      story: [
        "En rentrant chez moi la nuit. Il y a un homme derrière moi et je ne sais pas s'il est dangereux. J'ai peur, je ne veux pas trop me retourner pour ne pas attirer l'attention mais j'entends ses pas se rapprocher. Je ne sais pas s'il va me faire quelque chose. Je marche plus vite, j'accélère. J'imagine tous les pires scénarios possibles.",
        "Je ne veux pas avoir peur. Je ne veux pas être violée. Je ne veux pas être harcelée ni agressée.",
        "La justice ne fait rien pour que je me sente en sécurité. Après avoir dénoncé mon agresseur sexuel, j'attends toujours le procès. Après de nombreuses années, je dois encore revivre cette merde pour qu'au final, l'agresseur sexuel s'en sorte libre et impuni.",
        "Pouvoir respirer tranquillement et vivre sans l'hypervigilance qui caractérise le fait d'être une femme est un droit. Un droit qu'on nous a volé.",
        "C'est pourquoi Fight like a GYAL existe. Parce qu'attendre que le monde change n'est pas une option. Parce que notre sécurité ne peut pas dépendre des autres. Parce que la peur doit changer de camp.",
        "Nous ne promouvons pas la violence. Nous promouvons la confiance. La confiance de savoir que si quelque chose arrive, vous avez les outils pour vous défendre. La confiance de rentrer chez vous sans regarder par-dessus votre épaule toutes les trois secondes.",
        "C'est pour chaque femme qui a ressenti ce nœud dans l'estomac en marchant seule la nuit. Pour chaque femme qui a dû changer son itinéraire, ses vêtements, son emploi du temps pour se sentir plus en sécurité. Pour chaque femme à qui on a dit \"fais attention\" au lieu de dire aux hommes \"n'agresse pas.\"",
        "Nous nous battons comme des GYALs. Et maintenant, vous aussi vous pouvez."
      ],
      cta: "Rejoignez le mouvement",
      founder: "— La Fondatrice"
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="min-h-screen pt-20 bg-[#050505]" data-testid="about-page">
      {/* Hero */}
      <div className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF0099]/10 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Heart className="w-12 h-12 text-[#FF0099] mx-auto mb-6" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-black mb-4 tracking-tight">
            {c.title}
          </h1>
          <p className="text-[#FAFF00] font-accent text-xl" style={{ transform: 'rotate(-1deg)' }}>
            {c.subtitle}
          </p>
        </div>
      </div>

      {/* Story */}
      <div className="max-w-3xl mx-auto px-4 pb-20">
        <div className="relative">
          {/* Quote icon */}
          <Quote className="w-16 h-16 text-[#FF0099]/20 absolute -top-8 -left-4" />
          
          <div className="space-y-6 text-lg leading-relaxed">
            {c.story.map((paragraph, index) => (
              <p 
                key={index} 
                className={`${
                  index === 0 || index === 1 || index === 2 
                    ? 'text-white' 
                    : index === 4 || index === 7
                    ? 'text-[#FF0099] font-bold'
                    : 'text-[#A3A3A3]'
                }`}
              >
                {paragraph}
              </p>
            ))}
          </div>

          {/* Signature */}
          <p className="text-[#FAFF00] font-accent text-lg mt-12 text-right">
            {c.founder}
          </p>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Link to="/shop">
            <Button className="btn-primary text-lg px-8 py-6">
              <Shield className="w-5 h-5 mr-2" />
              {c.cta}
            </Button>
          </Link>
        </div>

        {/* Instagram */}
        <div className="mt-12 text-center">
          <a 
            href="https://instagram.com/fightlikeagyal" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#A3A3A3] hover:text-[#FF0099] transition-colors"
          >
            Follow our journey: <span className="font-bold">@fightlikeagyal</span>
          </a>
        </div>
      </div>
    </div>
  );
};
